import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import styles from './raffle.module.scss'
import cn from 'classnames'
import { Ticket, Dices, ExternalLink, Play } from 'lucide-react'
import useStore from 'state/knovStore'
import { ensureSpaceUsersLoaded } from 'state/spaceUsers'
import api from 'api/api'
import SatsIcon from 'components/shared/SatsIcon'
import SpinWheel from './SpinWheel'

/**
 * @typedef {Object} RaffleProps
 * @property {string} [mode]
 * @property {string} titleUserId
 * @property {any[]} [upvalues]
 * @property {() => void} [onRemove]
 * @property {string} answerId
 * @property {any} diceData
 */

/** @param {{ size?: number, className?: string }} props */
const RaffleWheelIcon = ({ size = 20, className }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={className}
    >
        {/* Outer circle */}
        <circle cx="12" cy="12" r="10" />
        {/* Dividing lines to create segments */}
        <line x1="12" y1="2" x2="12" y2="22" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <line x1="5.64" y1="5.64" x2="18.36" y2="18.36" />
        <line x1="5.64" y1="18.36" x2="18.36" y2="5.64" />
    </svg>
)

/** @param {RaffleProps} props */
const Raffle = ({ mode = 'preview', titleUserId, upvalues = [], onRemove, answerId, diceData }) => {
    const [participants, setParticipants] = useState([])
    const [totalAmount, setTotalAmount] = useState(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [isRunning, setIsRunning] = useState(false)
    const [showParticipantsPopup, setShowParticipantsPopup] = useState(false)
    const [showSpinWheel, setShowSpinWheel] = useState(false)
    const [localWinner, setLocalWinner] = useState(null)

    // Track if we're in the middle of a spin animation to prevent closing
    const [isSpinAnimating, setIsSpinAnimating] = useState(false)

    // Get users from store to match names
    const users = useStore(state => state.users)
    useEffect(() => {
        ensureSpaceUsersLoaded()
    }, [])

    // Get winner from dice data - this is the authoritative winner from server
    const winner = diceData?.winner

    // Preserve SpinWheel open state across mode changes
    useEffect(() => {
        // If mode changes from 'ready' to 'winner' while SpinWheel is open,
        // we want to keep it open. This handles the websocket update case.
        if (diceData?.status === 'winner' && diceData?.winner) {
            // Update local winner if we don't have it yet
            if (!localWinner) {
                setLocalWinner(diceData.winner)
            }
        }
    }, [diceData])

    useEffect(() => {
        if (upvalues) {
            processUpvalues()
        }
    }, [upvalues, mode, users])

    const processUpvalues = () => {
        try {
            setLoading(true)

            // Ensure upvalues is an array
            const upvalueArray = Array.isArray(upvalues) ? upvalues : []

            // Filter out upvalues from the title author
            const validUpvalues = upvalueArray.filter(upvalue => {
                return upvalue.from_user_id && titleUserId && upvalue.from_user_id !== titleUserId
            })

            // Transform upvalues into participant format
            const participantData = validUpvalues.map(upvalue => {
                // Look up user in store by from_user_id
                const user = users && users.find(u => u.id === upvalue.from_user_id)
                const userName =
                    user?.name || upvalue.from_user_name || upvalue.from_user?.name || 'Unknown'

                const amount = parseFloat(upvalue.amount) || 0
                const fee = parseFloat(upvalue.fee) || 0

                return {
                    userName,
                    userId: upvalue.from_user_id,
                    amount: amount - fee, // Store net amount after fee
                }
            })

            // Group by user and sum amounts
            const userAmounts = {}
            participantData.forEach(p => {
                if (!userAmounts[p.userId]) {
                    userAmounts[p.userId] = {
                        userName: p.userName,
                        userId: p.userId,
                        amount: 0,
                    }
                }
                userAmounts[p.userId].amount += p.amount
            })

            const finalParticipants = Object.values(userAmounts)

            setParticipants(finalParticipants)
            setTotalAmount(finalParticipants.reduce((sum, p) => sum + p.amount, 0))
            setLoading(false)

            if (finalParticipants.length === 0) {
                setError('No eligible participants found')
            }
        } catch (err) {
            setError('Failed to process upvalue data')
            setLoading(false)
        }
    }

    const handleRunRaffle = async e => {
        if (e) e.stopPropagation()
        if (!answerId || isRunning || mode !== 'ready') return

        // Additional client-side protection: only title owner can run
        if (gon?.currentUser?.id !== titleUserId) {
            setError('Only the title owner can run the raffle')
            return
        }

        try {
            setIsRunning(true)
            const response = await api.runRaffle(answerId)
            console.log('Run raffle response:', response)

            if (response.success) {
                console.log('Raffle run successfully')

                // Try to extract the winner from the response
                const answer = response.answer
                const diceData = answer?.dice || answer?.answer?.dice
                const responseWinner = diceData?.winner

                if (responseWinner) {
                    // Store the winner locally if we got it in the response
                    setLocalWinner(responseWinner)
                    console.log('Winner found in response:', responseWinner)
                }

                // Always show the spin wheel after successful raffle run
                // The winner will come either from localWinner or through props update
                setShowSpinWheel(true)
            } else {
                setError(response.error || 'Failed to run raffle')
            }
        } catch (error) {
            console.error('Error running raffle:', error)
            setError('Failed to run raffle')
        } finally {
            setIsRunning(false)
        }
    }

    const renderStatusButton = () => {
        switch (mode) {
            case 'preview':
                return (
                    <div className={cn(styles.statusButton, styles.newStatus)}>
                        <Dices size={16} className={styles.statusIcon} />
                        <span>Preview</span>
                    </div>
                )

            case 'ready':
                return (
                    <div className={cn(styles.statusButton, styles.newStatus)}>
                        <Dices size={16} className={styles.statusIcon} />
                        <span>Ready</span>
                    </div>
                )

            case 'pending':
                return (
                    <div className={cn(styles.statusButton, styles.pendingStatus)}>
                        <Dices size={16} className={cn(styles.statusIcon, styles.spinning)} />
                        <span>Running raffle...</span>
                    </div>
                )

            case 'winner':
                if (!winner) {
                    return (
                        <div className={cn(styles.statusButton, styles.errorStatus)}>
                            <span className={styles.statusIcon}>⚠️</span>
                            <span>No winner - no participants</span>
                        </div>
                    )
                }

                // Find the winner in participants to get consistent net amount
                const winnerParticipant = participants.find(
                    p => p.userId === winner.user_id || p.userName === winner.user_name,
                )
                // Use net amount from participants for consistent percentage with other displays
                const winnerNetAmount = winnerParticipant?.amount || winner.amount
                const winnerChance =
                    totalAmount > 0 ? ((winnerNetAmount / totalAmount) * 100).toFixed(1) : '100'

                return (
                    <div className={cn(styles.statusButton, styles.winnerStatus)}>
                        <div className={styles.winnerContent}>
                            <div className={styles.celebrationEmojis}>
                                <span className={styles.emoji}>🎉</span>
                            </div>
                            <div className={styles.winnerText}>
                                <span className={styles.winnerLabel}>WINNER</span>
                                <span className={styles.winnerName}>@{winner.user_name}</span>
                                <span className={styles.winnerChance}>{winnerChance}% chance</span>
                            </div>
                            <div className={styles.celebrationEmojis}>
                                <span className={styles.emoji}>🎉</span>
                            </div>
                        </div>
                    </div>
                )

            case 'error':
                return (
                    <div className={cn(styles.statusButton, styles.errorStatus)}>
                        <span className={styles.statusIcon}>⚠️</span>
                        <span>Raffle failed</span>
                    </div>
                )

            default:
                return null
        }
    }

    const renderParticipantsPopup = () => {
        //console.log('renderParticipantsPopup called, showParticipantsPopup:', showParticipantsPopup)
        if (!showParticipantsPopup) return null

        return ReactDOM.createPortal(
            <div
                className={styles.popupOverlay}
                onClick={e => {
                    e.stopPropagation()
                    setShowParticipantsPopup(false)
                }}
            >
                <div
                    className={styles.participantsPopup}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Raffle participants"
                    onClick={e => e.stopPropagation()}
                >
                    <div className={styles.popupHeader}>
                        <h3>Raffle Participants</h3>
                        <button
                            className={styles.closeButton}
                            onClick={e => {
                                e.stopPropagation()
                                setShowParticipantsPopup(false)
                            }}
                        >
                            ✕
                        </button>
                    </div>
                    <div className={styles.popupContent}>
                        {participants.length > 0 ? (
                            <div className={styles.participantsList}>
                                {participants.map((p, i) => {
                                    // Use net amount (after fee) for consistent percentage calculation
                                    const chance =
                                        totalAmount > 0
                                            ? ((p.amount / totalAmount) * 100).toFixed(1)
                                            : '0'
                                    return (
                                        <div key={i} className={styles.participantRow}>
                                            <span className={styles.participantName}>
                                                @{p.userName}
                                            </span>
                                            <div className={styles.participantStats}>
                                                <span className={styles.participantChance}>
                                                    {chance}%
                                                </span>
                                                <span className={styles.participantAmount}>
                                                    <SatsIcon
                                                        contextStyles={styles.smallSatsIcon}
                                                        showBars={false}
                                                    />
                                                    {Math.floor(p.amount).toLocaleString()}
                                                </span>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        ) : (
                            <div className={styles.noParticipants}>
                                No eligible participants yet
                            </div>
                        )}
                    </div>
                </div>
            </div>,
            document.body,
        )
    }

    const handleSpinComplete = async () => {
        // Only finalize if this is the initial spin (mode is not already 'winner')
        if (mode !== 'winner') {
            // After spin completes, finalize the raffle by updating status to 'winner'
            const winnerToFinalize = localWinner || winner
            if (winnerToFinalize && answerId) {
                try {
                    console.log('Finalizing raffle with winner:', winnerToFinalize)
                    const response = await api.finalizeRaffle(answerId)
                    if (response.success) {
                        console.log('Raffle finalized successfully')
                        // The websocket broadcast will update the answer automatically
                        // Don't close the wheel - let user close it manually
                    }
                } catch (error) {
                    console.error('Error finalizing raffle:', error)
                }
            } else {
                console.error('Cannot finalize raffle - no winner or answerId', {
                    winnerToFinalize,
                    answerId,
                })
            }
        }

        // Don't auto-close - let the user close it when they want
    }

    if (loading) {
        return (
            <div className={cn(styles.raffle, styles[mode])} data-testid="raffle-answer">
                <div className={styles.loading}>
                    <div className={styles.loader}></div>
                    <span>Loading upvalue data...</span>
                </div>
            </div>
        )
    }

    if (error && participants.length === 0) {
        return (
            <div className={cn(styles.raffle, styles.error)} data-testid="raffle-answer">
                <div className={styles.error}>
                    <span className={styles.errorIcon}>⚠️</span>
                    <span>{error}</span>
                </div>
            </div>
        )
    }

    return (
        <div
            className={cn(styles.raffle, styles[mode])}
            data-testid="raffle-answer"
            onClick={e => e.stopPropagation()}
        >
            <div className={styles.preview}>
                <div className={styles.header}>
                    <RaffleWheelIcon size={20} className={styles.icon} />
                    <span className={styles.title}>Title Raffle</span>
                    {onRemove && (
                        <button
                            className={styles.removeBtn}
                            onClick={e => {
                                e.stopPropagation()
                                onRemove()
                            }}
                        >
                            ✕
                        </button>
                    )}
                </div>
                <div className={styles.stats}>
                    <div className={styles.jackpotStat}>
                        <span className={styles.jackpotLabel}>Jackpot</span>
                        <div className={styles.jackpotValue}>
                            <SatsIcon contextStyles={styles.satsIcon} showBars={false} />
                            <span className={styles.jackpotAmount}>
                                {Math.floor(totalAmount).toLocaleString()}
                            </span>
                        </div>
                    </div>
                    <div
                        className={cn(
                            styles.participantsBanner,
                            participants.length > 0 && styles.clickable,
                        )}
                        onClick={
                            participants.length > 0
                                ? e => {
                                      e.stopPropagation()
                                      console.log('Participants banner clicked, showing popup')
                                      setShowParticipantsPopup(true)
                                  }
                                : undefined
                        }
                    >
                        <span className={styles.participantCount}>
                            {participants.length}{' '}
                            {participants.length === 1 ? 'Participant' : 'Participants'}
                        </span>
                        {participants.length > 0 && (
                            <ExternalLink size={14} className={styles.viewMore} />
                        )}
                    </div>
                </div>
                {(mode === 'preview' || mode === 'ready' || mode === 'winner') && (
                    <div className={styles.playButtonContainer}>
                        <button
                            className={styles.playButton}
                            onClick={async e => {
                                e.stopPropagation()
                                if (mode === 'preview') return

                                if (mode === 'ready') {
                                    const isOwner = gon?.currentUser?.id === titleUserId
                                    if (isOwner) {
                                        // Run the raffle first, then show the wheel with predetermined winner
                                        await handleRunRaffle()
                                        // Don't show wheel here - let the state update trigger it
                                    }
                                } else if (mode === 'winner') {
                                    setShowSpinWheel(true)
                                }
                            }}
                            disabled={
                                mode === 'preview' ||
                                isRunning ||
                                (mode === 'ready' && gon?.currentUser?.id !== titleUserId) ||
                                (mode !== 'ready' && mode !== 'winner')
                            }
                        >
                            <Play size={24} />
                            <span>
                                {mode === 'preview'
                                    ? 'Run'
                                    : mode === 'ready'
                                    ? gon?.currentUser?.id === titleUserId
                                        ? isRunning
                                            ? 'Loading...'
                                            : 'Run'
                                        : 'Waiting...'
                                    : 'Play'}
                            </span>
                        </button>
                    </div>
                )}
                {renderStatusButton()}
            </div>
            {renderParticipantsPopup()}
            {showSpinWheel && (
                <SpinWheel
                    participants={participants}
                    totalAmount={totalAmount}
                    predeterminedWinner={localWinner || winner}
                    onClose={() => {
                        setShowSpinWheel(false)
                        setIsSpinAnimating(false)
                    }}
                    onSpinStart={() => setIsSpinAnimating(true)}
                    onSpinComplete={() => {
                        setIsSpinAnimating(false)
                        handleSpinComplete()
                    }}
                />
            )}
        </div>
    )
}

export default Raffle
